class StOpenEyes extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('section-id');
    this.isVideo = this.getAttribute('is-video') === 'true';
    this.initTimer = null;
    this.initAttempts = 0;
    this.syncFrame = null;
    this.initialSyncTimers = [];
    this.restoreAnchor = null;
    this.resizeTimer = null;
    this.timeline = null;
    this.atcObserver = null;
    this.atcResizeObserver = null;
    this.onResize = this.onResize.bind(this);
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleBottomOffsetChange = this.handleBottomOffsetChange.bind(this);
    this.handleUserInteraction = this.handleUserInteraction.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
  }

  connectedCallback() {
    this.restoreAnchor = this.readRestoreAnchor();
    this.scheduleTimelineSetup();
    this.motionPreference.addEventListener('change', this.onResize);

    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('wheel', this.handleUserInteraction, { passive: true });
    window.addEventListener('touchstart', this.handleUserInteraction, { passive: true });
    window.addEventListener('pointerdown', this.handleUserInteraction, { passive: true });
    window.addEventListener('keydown', this.handleUserInteraction);
    this.armInitialPositionSync();
    this.observeBottomBar();
  }

  disconnectedCallback() {
    window.clearTimeout(this.initTimer);
    window.clearTimeout(this.resizeTimer);
    this.cancelInitialPositionSync();
    if (this.syncFrame) {
      window.cancelAnimationFrame(this.syncFrame);
      this.syncFrame = null;
    }
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('wheel', this.handleUserInteraction);
    window.removeEventListener('touchstart', this.handleUserInteraction);
    window.removeEventListener('pointerdown', this.handleUserInteraction);
    window.removeEventListener('keydown', this.handleUserInteraction);
    this.motionPreference.removeEventListener('change', this.onResize);

    if (this.atcObserver) {
      this.atcObserver.disconnect();
      this.atcObserver = null;
    }


    this.atcResizeObserver?.disconnect();
    this.atcResizeObserver = null;
    this.destroyTimeline();
    this.removeAttribute('data-animated');
  }

  isHidden() {
    const wrapper = this.closest('.scroll-trigger-open-eyes') || this;

    for (let element = wrapper; element && element.nodeType === Node.ELEMENT_NODE; element = element.parentElement) {
      if (window.getComputedStyle(element).display === 'none') return true;
    }

    return wrapper.getClientRects().length === 0;
  }

  onResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      this.scheduleTimelineSetup();
    }, 150);
  }

  scheduleTimelineSetup(delay = 0) {
    window.clearTimeout(this.initTimer);
    this.initTimer = window.setTimeout(() => {
      this.initTimer = null;

      if (!this.isConnected) return;
      if (this.isHidden() || this.motionPreference.matches) {
        this.destroyTimeline(true);
        this.removeAttribute('data-animated');
        this.removeAttribute('data-initializing');
        return;
      }

      if (!window.gsap || !window.ScrollTrigger) {
        this.initAttempts += 1;
        this.scheduleTimelineSetup(Math.min(1000, this.initAttempts * 100));
        return;
      }

      this.initAttempts = 0;
      this.setupTimeline();
      this.syncTimelineToScroll();
    }, delay);
  }

  syncTimelineToScroll(refresh = true) {
    if (this.syncFrame) window.cancelAnimationFrame(this.syncFrame);

    this.syncFrame = window.requestAnimationFrame(() => {
      this.syncFrame = null;
      const scrollTrigger = this.getScrollTrigger();

      if (!scrollTrigger) return;

      if (refresh) {
        scrollTrigger.refresh();
      }
      scrollTrigger.update();
      const scrubTween = scrollTrigger.getTween?.();
      scrubTween?.progress(1);
      this.timeline.progress(scrollTrigger.progress);
      this.syncMediaLayout();
      this.removeAttribute('data-initializing');
    });
  }

  handleUserInteraction() {
    this.cancelInitialPositionSync();
    this.restoreAnchor = null;
  }

  handlePageShow() {
    this.restoreAnchor = this.readRestoreAnchor();
    this.armInitialPositionSync();
  }

  handlePageHide() {
    const nextSection = this.getNextSection();
    const scrollTrigger = this.getScrollTrigger();
    if (!nextSection || !scrollTrigger || window.scrollY < scrollTrigger.end) return;

    const nextSectionTop = nextSection.getBoundingClientRect().top + window.scrollY;
    try {
      window.sessionStorage.setItem(this.restoreStorageKey(), JSON.stringify({
        path: window.location.pathname,
        offset: window.scrollY - nextSectionTop,
        savedAt: Date.now(),
      }));
    } catch (error) {
      this.restoreAnchor = null;
    }
  }

  armInitialPositionSync() {
    this.cancelInitialPositionSync();
    [0, 100, 500, 1500, 3500].forEach((delay) => {
      const timer = window.setTimeout(() => {
        this.initialSyncTimers = this.initialSyncTimers.filter((pendingTimer) => pendingTimer !== timer);
        this.restoreInitialPosition();
        this.syncTimelineToScroll();
      }, delay);
      this.initialSyncTimers.push(timer);
    });
  }

  cancelInitialPositionSync() {
    this.initialSyncTimers.forEach((timer) => window.clearTimeout(timer));
    this.initialSyncTimers = [];
  }

  restoreStorageKey() {
    return `open-eyes-scroll:${this.sectionId}`;
  }

  readRestoreAnchor() {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(this.restoreStorageKey()));
      if (stored?.path !== window.location.pathname || Date.now() - stored.savedAt > 30000) return null;
      return stored;
    } catch (error) {
      return null;
    }
  }

  restoreInitialPosition() {
    if (!this.restoreAnchor) return;
    const nextSection = this.getNextSection();
    if (!nextSection) return;

    const nextSectionTop = nextSection.getBoundingClientRect().top + window.scrollY;
    window.scrollTo(0, nextSectionTop + this.restoreAnchor.offset);
  }

  getNextSection() {
    const section = this.closest('.shopify-section, [id^="shopify-section-"]');
    return section?.nextElementSibling || null;
  }

  setupTimeline() {
    if (!window.gsap || !window.ScrollTrigger) return;
    if (this.isHidden()) {
      this.destroyTimeline(true);
      return;
    }

    this.destroyTimeline(true);
    this.setAttribute('data-animated', '');
    this.syncBottomOffset();
    this.syncBottomContentHeight();
    this.clampBottomContentItemBottom();

    const screenWidth = window.innerWidth;
    const topOffset = this.readCssPixel('--open-eyes-top-offset', 64);
    const bottomOffset = this.readCssPixel('--open-eyes-bottom-offset', 0);
    const fullscreenHeight = Math.max(0, window.innerHeight - topOffset - bottomOffset, screenWidth >= 768 && screenWidth < 1920 && window.innerHeight < 1080 ? screenWidth * 0.5 : 0);
    const wrapper = this.querySelector('.open-eyes-wrapper');
    const titleWrapper = this.querySelector('.title-wrapper');
    const playWrapper = this.querySelector('.player-wrapper');
    const contentWrapper = this.querySelector('.content-wrapper');
    const innerContent = this.querySelector('.player-inner-content');
    const bottomContent = this.querySelector('.player-media-bottom-content');
    const featureStrip = this.querySelector('.open-eyes-feature-strip');
    const player = this.querySelector('.player');
    const playerMask = this.querySelector('.player-mask');
    const openTextItems = Array.from(this.querySelectorAll('.open-eyes-wrapper .oetl'));
    const collapseTargets = [titleWrapper, contentWrapper].filter(Boolean);
    const mediaContents = Array.from(this.querySelectorAll('.player-media-content'));
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!wrapper || !playWrapper || !innerContent || !playerMask) return;

    if (mediaContents.length && !reduceMotion) {
      window.gsap.set(mediaContents, { autoAlpha: 0 });
    }

    this.timeline = window.gsap.timeline({
      scrollTrigger: {
        // The sticky child moves in document coordinates as the page scrolls.
        // Measure the normal-flow wrapper so refresh cannot shift the range.
        trigger: wrapper,
        scrub: 1,
        start: `top top+=${topOffset}`,
        // Finish before sticky release so the complete scene remains readable.
        end: () => `+=${Math.max(1, (wrapper.offsetHeight - fullscreenHeight) * 0.8)}`,
        invalidateOnRefresh: true,
      },
    });

    this.timeline
      .fromTo(openTextItems, {
        opacity: 0,
        x: 0.5 * screenWidth,
      }, {
        opacity: 1,
        x: 0,
        ease: 'power1.out',
        duration: 0.6,
      })
      .to(playWrapper, {
        opacity: 1,
        width: '100%',
        ease: 'power1.out',
        duration: 0.6,
      }, 0)
      .addLabel('text-complete')
      .to(playerMask, {
        backgroundPositionX: '400%',
        opacity: 1,
        duration: 0.4,
        onComplete: () => {
          if (player) player.classList.add('center');
          innerContent.style.zIndex = '2';
        },
        onReverseComplete: () => {
          if (player) player.classList.remove('center');
          innerContent.style.zIndex = '0';
        },
      })
      .to(playWrapper, {
        height: 40,
        duration: 0.2,
      })
      .to(innerContent, {
        opacity: 0,
        duration: 0.2,
        onComplete: () => {
          playWrapper.style.backgroundColor = 'rgba(0,0,0,0.4)';
        },
      }, '>0.2')
      .to(playWrapper, {
        height: fullscreenHeight,
        width: '100vw',
        borderRadius: 0,
        duration: 0.45,
        ease: 'power2.out',
        backgroundColor: 'rgba(0,0,0,0)',
        onStart: () => {
          playWrapper.classList.add('is-fullscreen');
        },
        onReverseComplete: () => {
          playWrapper.classList.remove('is-fullscreen');
        },
        onComplete: () => {
          if (!this.isVideo) return;
          const video = this.querySelector('.player-wrapper video');
          if (video) video.play();
        },
      })
      .addLabel('fullscreen-start', '<');

    this.timeline.fromTo(
        collapseTargets,
        {
          maxHeight(index, element) {
            return element.scrollHeight;
          },
          opacity: 1,
          marginTop: 0,
          marginBottom: 0,
          paddingTop: 0,
          paddingBottom: 0,
        },
        {
          onStart() {
            collapseTargets.forEach((element) => {
              element.style.overflow = 'hidden';
            });
          },
          maxHeight: 0,
          opacity: 0,
          marginTop: 0,
          marginBottom: 0,
          paddingTop: 0,
          paddingBottom: 0,
          duration: 0.12,
          ease: 'power2.in',
          onComplete() {
            collapseTargets.forEach((element) => {
              element.style.pointerEvents = 'none';
            });
          },
          onReverseComplete() {
            collapseTargets.forEach((element) => {
              element.style.pointerEvents = '';
              element.style.overflow = '';
              window.gsap.set(element, {
                clearProps: 'maxHeight,opacity,marginTop,marginBottom,paddingTop,paddingBottom',
              });
            });
          },
        },
        '>',
      )
      .addLabel('video')
      .to(playerMask, {
        duration: 0.4,
        opacity: 0.7,
        onComplete: () => {
          playerMask.style.opacity = '0';
        },
        onReverseComplete: () => {
          playerMask.style.opacity = '0';
        },
      }, 1)
      .to(this.querySelectorAll('.subtitle.oetl'), {
        duration: 0.2,
        fontWeight: 700,
        onReverseComplete: () => {
          if (!this.isVideo) return;
          const video = this.querySelector('.player-wrapper video');
          if (video) video.pause();
        },
      });

    if (!reduceMotion && mediaContents.length) {
      this.timeline.to(mediaContents, {
        autoAlpha: 1,
        duration: 0.4,
        ease: 'power2.out',
      }, '>');
    }

    if (bottomContent) {
      if (featureStrip && !reduceMotion) {
        this.timeline
          .to(bottomContent, {
            opacity: 1,
            duration: 0.3,
            ease: 'power1.inOut',
          }, '>')
          .fromTo(featureStrip, {
            autoAlpha: 0,
            y: 20,
          }, {
            autoAlpha: 1,
            y: 0,
            duration: 0.3,
            ease: 'power1.inOut',
          }, '<');
      } else {
        this.timeline.to(bottomContent, {
          opacity: 1,
          duration: 0.2,
          translateY: -20,
        }, '>');
      }
    }

    this.timeline.to(playerMask, {
      duration: 0.1,
      opacity: 0,
    }).addLabel('media-ready');

  }

  readCssPixel(propertyName, fallback = 0) {
    const host = this.closest('.scroll-trigger-open-eyes') || this;
    const styles = window.getComputedStyle(host);
    const raw = styles.getPropertyValue(propertyName);
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  syncBottomOffset() {
    const host = this.closest('.scroll-trigger-open-eyes') || this;
    const addToCartBar = document.querySelector('.add-to-cart-bar-wrapper');
    let bottomOffset = 0;

    if (addToCartBar && !addToCartBar.classList.contains('hidden')) {
      const styles = window.getComputedStyle(addToCartBar);

      if (styles.display !== 'none' && styles.visibility !== 'hidden') {
        bottomOffset = Math.ceil(addToCartBar.getBoundingClientRect().height);
      }
    }

    const previousOffset = this.readCssPixel('--open-eyes-bottom-offset', 0);
    host.style.setProperty('--open-eyes-bottom-offset', `${bottomOffset}px`);
    if (previousOffset !== bottomOffset && this.timeline) this.onResize();
  }

  handleBottomOffsetChange() {
    this.syncBottomOffset();
  }

  syncBottomContentHeight() {
    const host = this.closest('.scroll-trigger-open-eyes') || this;
    const desktopBottomContent = this.querySelector('.player-media-wrap.sm\\:block .player-media-bottom-content');

    if (window.innerWidth <= 700 || !desktopBottomContent) {
      host.style.removeProperty('--open-eyes-bottom-content-height');
      desktopBottomContent?.style.removeProperty('height');
      return;
    }

    const item = desktopBottomContent.querySelector('.player-media-bottom-content-item');
    const measuredHeight = item
      ? Math.max(item.offsetHeight, item.scrollHeight)
      : desktopBottomContent.scrollHeight;
    const height = Math.ceil(measuredHeight / 2);

    host.style.setProperty('--open-eyes-bottom-content-height', `${height}px`);
  }

  clampBottomContentItemBottom() {
    const items = this.querySelectorAll('.player-media-bottom-content-item');

    items.forEach((item) => {
      const computedBottom = Number.parseFloat(window.getComputedStyle(item).bottom);

      if (Number.isFinite(computedBottom) && computedBottom < 0) {
        item.style.bottom = '0px';
      }
    });
  }

  observeBottomBar() {
    const addToCartBar = document.querySelector('.add-to-cart-bar-wrapper');

    if (!addToCartBar) return;

    this.atcObserver = new MutationObserver(() => {
      this.handleBottomOffsetChange();
    });

    this.atcResizeObserver = new ResizeObserver(this.handleBottomOffsetChange);
    this.atcResizeObserver.observe(addToCartBar);

    this.atcObserver.observe(addToCartBar, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  destroyTimeline(clearAnimatedProps = false) {
    if (this.timeline) {
      if (this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill();
      this.timeline.kill();
      this.timeline = null;
    }

    if (!clearAnimatedProps || !window.gsap) return;

    const playWrapper = this.querySelector('.player-wrapper');
    const innerContent = this.querySelector('.player-inner-content');
    const player = this.querySelector('.player');
    const playerMask = this.querySelector('.player-mask');
    const animatedItems = Array.from(this.querySelectorAll('.open-eyes-wrapper .oetl'));
    const collapseTargets = Array.from(this.querySelectorAll('.title-wrapper, .content-wrapper'));
    const bottomContent = this.querySelector('.player-media-bottom-content');
    const featureStrip = this.querySelector('.open-eyes-feature-strip');
    const mediaContents = Array.from(this.querySelectorAll('.player-media-content'));
    const targets = [
      ...animatedItems,
      ...collapseTargets,
      playWrapper,
      innerContent,
      playerMask,
      bottomContent,
      featureStrip,
      ...mediaContents,
    ].filter(Boolean);

    if (player) player.classList.remove('center');
    if (playWrapper) playWrapper.classList.remove('is-fullscreen');

    window.gsap.set(targets, {
      clearProps:
        'opacity,visibility,autoAlpha,x,y,width,height,borderRadius,backgroundColor,backgroundPositionX,maxHeight,marginTop,marginBottom,paddingTop,paddingBottom,translateY,fontWeight',
    });

    collapseTargets.forEach((element) => {
      element.style.pointerEvents = '';
      element.style.overflow = '';
    });

    if (innerContent) innerContent.style.zIndex = '';
  }

  syncMediaLayout() {
    if (!this.timeline) return;

    // Refresh can seek past callbacks; derive layout from the restored playhead.
    const fullscreenStart = this.timeline.labels['fullscreen-start'];
    const fullscreen = Number.isFinite(fullscreenStart) && this.timeline.time() > fullscreenStart;
    this.querySelector('.player-wrapper')?.classList.toggle('is-fullscreen', fullscreen);
  }

  getScrollTrigger() {
    return this.timeline && this.timeline.scrollTrigger ? this.timeline.scrollTrigger : null;
  }

}

if (!customElements.get('st-open-eyes')) {
  customElements.define('st-open-eyes', StOpenEyes);
}
